// SolmanStatusPanel.jsx — status proses SolMan Docs (Sync / Fetch / OCR / Upload S3 / Delete local)
// Backend: GET /api/solman-docs/status[?strucId=]  (backend/solmanDocs.js)
//   mode="overview" → tabel semua project (klik baris = pilih project)
//   mode="detail"   → run terakhir per step, riwayat run, dokumen bermasalah + retry
import React, { useCallback, useEffect, useState } from 'react';
import { Activity, AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, Clock, Loader2, RefreshCw, RotateCcw } from 'lucide-react';

const authHeaders = (json = false) => {
  const h = { Authorization: 'Bearer ' + (localStorage.getItem('auth_token') || '') };
  if (json) h['Content-Type'] = 'application/json';
  return h;
};
async function api(url, opts = {}) {
  const r = await fetch(url, { ...opts, headers: { ...authHeaders(!!opts.body), ...(opts.headers || {}) } });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || ('HTTP ' + r.status));
  return j;
}
const fmtBytes = (n) => {
  n = Number(n || 0);
  if (n < 1024) return n + ' B';
  if (n < 1048576) return (n / 1024).toFixed(0) + ' KB';
  if (n < 1073741824) return (n / 1048576).toFixed(1) + ' MB';
  return (n / 1073741824).toFixed(2) + ' GB';
};
// started_at ada yang ISO (UTC, "…Z") dan ada yang "YYYY-MM-DD HH:MM:SS" (UTC tanpa zona).
const toDate = (t) => {
  if (!t) return null;
  const s = String(t);
  const d = new Date(/Z$|[+-]\d\d:?\d\d$/.test(s) ? s : s.replace(' ', 'T') + 'Z');
  return isNaN(d) ? null : d;
};
const fmtTime = (t) => {
  const d = toDate(t);
  return d ? d.toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '–';
};
const ago = (t) => {
  const d = toDate(t);
  if (!d) return '';
  const m = Math.round((Date.now() - d.getTime()) / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return m + ' min ago';
  if (m < 1440) return Math.round(m / 60) + ' h ago';
  return Math.round(m / 1440) + ' d ago';
};

const STEP_ORDER = [
  ['pipeline', 'Pipeline'], ['sync', 'Sync list'], ['fetch', 'Fetch PDFs'], ['ocr', 'OCR'],
  ['s3', 'Upload S3'], ['kb', 'Knowledge Base text'], ['kb-ingest', 'KB ingest (Bedrock)'], ['s3-free', 'Delete local'],
];
const STATE = {
  done: { label: 'Complete', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  partial: { label: 'In progress', cls: 'bg-sky-50 text-sky-700 border-sky-200' },
  todo: { label: 'Not started', cls: 'bg-slate-50 text-slate-500 border-slate-200' },
  issue: { label: 'Has issues', cls: 'bg-amber-50 text-amber-800 border-amber-200' },
};
function RunBadge({ status }) {
  const s = String(status || '').toLowerCase();
  const cls = s === 'running' ? 'bg-sky-50 text-sky-700 border-sky-200'
    : ['ok', 'done', 'complete'].includes(s) ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : ['warn', 'partial'].includes(s) ? 'bg-amber-50 text-amber-800 border-amber-200'
    : ['error', 'failed'].includes(s) ? 'bg-rose-50 text-rose-700 border-rose-200'
    : 'bg-slate-50 text-slate-600 border-slate-200';
  return <span className={`text-[11px] px-1.5 py-0.5 rounded border ${cls}`}>{status || '–'}</span>;
}
const Num = ({ v, warn, good }) => (
  <span className={v ? (warn ? 'text-rose-600 font-medium' : good ? 'text-emerald-700' : 'text-slate-700') : 'text-slate-300'}>{v || 0}</span>
);

function useStatus(strucId, refreshMs) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await api('/api/solman-docs/status' + (strucId ? '?strucId=' + encodeURIComponent(strucId) : '')));
      setErr(null);
    } catch (e) { setErr(e.message); } finally { setLoading(false); }
  }, [strucId]);
  useEffect(() => {
    load();
    const t = setInterval(() => { if (!document.hidden) load(); }, refreshMs);
    return () => clearInterval(t);
  }, [load, refreshMs]);
  return { data, err, loading, load };
}

function Overview({ selected, onSelect }) {
  const [open, setOpen] = useState(() => { try { return localStorage.getItem('solman_status_open') !== '0'; } catch (_) { return true; } });
  const [showHidden, setShowHidden] = useState(false);
  const { data, err, loading, load } = useStatus('', 20000);
  const toggle = () => { setOpen((o) => { try { localStorage.setItem('solman_status_open', o ? '0' : '1'); } catch (_) {} return !o; }); };
  const rows = ((data && data.projects) || []).filter((p) => showHidden || !p.hidden);
  const tot = rows.reduce((a, p) => {
    for (const k of ['docs', 'fetched', 'not_fetched', 'fetch_err', 'ocr_done', 'ocr_err', 's3_ok', 's3_err', 's3_pending', 'local_files', 'local_bytes']) a[k] = (a[k] || 0) + (p[k] || 0);
    return a;
  }, {});
  const issues = rows.filter((p) => p.state === 'issue').length;
  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
      <div className="px-3 py-2 flex items-center justify-between gap-2 border-b border-slate-100">
        <button type="button" onClick={toggle} className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />} <Activity size={14} /> Process status
          {data && <span className="normal-case font-normal text-slate-400 tracking-normal">
            · {rows.filter((p) => p.state === 'done').length}/{rows.length} complete
            {issues ? <span className="text-amber-700"> · {issues} with issues</span> : null}
            {tot.local_files ? <> · {tot.local_files} local files ({fmtBytes(tot.local_bytes)})</> : null}
          </span>}
        </button>
        <div className="flex items-center gap-3 text-xs">
          {data && data.disk && <span className={data.disk.free < data.disk.reserve * 2 ? 'text-rose-600 font-medium' : 'text-slate-400'}>Disk free {fmtBytes(data.disk.free)}</span>}
          <label className="flex items-center gap-1 text-slate-500"><input type="checkbox" checked={showHidden} onChange={(e) => setShowHidden(e.target.checked)} /> hidden</label>
          <button type="button" onClick={load} title="Refresh" className="p-1 rounded hover:bg-slate-100 text-slate-500">
            {loading ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
          </button>
        </div>
      </div>
      {open && (err ? <div className="p-3 text-xs text-rose-600">{err}</div> : !data ? (
        <div className="p-3 text-xs text-slate-500 flex items-center gap-2"><Loader2 size={13} className="animate-spin" /> Loading…</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-slate-500">
              <tr className="text-left">
                <th className="px-3 py-1.5 font-medium">Project</th>
                <th className="px-2 py-1.5 font-medium text-right">Docs</th>
                <th className="px-2 py-1.5 font-medium text-right">Fetched</th>
                <th className="px-2 py-1.5 font-medium text-right" title="Not fetched from SAP yet">To fetch</th>
                <th className="px-2 py-1.5 font-medium text-right" title="OCR done / failed">OCR ok / fail</th>
                <th className="px-2 py-1.5 font-medium text-right" title="In S3 / upload failed / fetched but not uploaded yet">S3 ok / fail / pending</th>
                <th className="px-2 py-1.5 font-medium text-right" title="Files still on the server disk">Local</th>
                <th className="px-2 py-1.5 font-medium">Last run</th>
                <th className="px-3 py-1.5 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => {
                const st = STATE[p.state] || STATE.todo;
                const running = data.pipe && data.pipe.running && data.pipe.strucId === p.struc_id;
                return (
                  <tr key={p.struc_id} onClick={() => onSelect && onSelect(p.struc_id)}
                    className={`border-t border-slate-100 cursor-pointer hover:bg-slate-50 ${selected === p.struc_id ? 'bg-indigo-50/60' : ''}`}>
                    <td className="px-3 py-1.5 text-slate-700 max-w-[260px] truncate" title={p.name || p.struc_id}>{p.project_id || p.name || p.struc_id}</td>
                    <td className="px-2 py-1.5 text-right text-slate-700">{p.docs}</td>
                    <td className="px-2 py-1.5 text-right"><Num v={p.fetched} good={p.fetched === p.docs} />{p.fetch_err ? <span className="text-rose-600"> +{p.fetch_err} err</span> : null}</td>
                    <td className="px-2 py-1.5 text-right"><Num v={p.not_fetched} /></td>
                    <td className="px-2 py-1.5 text-right"><Num v={p.ocr_done} /> / <Num v={p.ocr_err} warn /></td>
                    <td className="px-2 py-1.5 text-right"><Num v={p.s3_ok} good={p.s3_ok === p.fetched && p.fetched > 0} /> / <Num v={p.s3_err} warn /> / <Num v={p.s3_pending} /></td>
                    <td className="px-2 py-1.5 text-right text-slate-600">{p.local_files ? <>{p.local_files} · {fmtBytes(p.local_bytes)}</> : <span className="text-slate-300">0</span>}</td>
                    <td className="px-2 py-1.5 text-slate-500 whitespace-nowrap">
                      {p.last_run ? <><RunBadge status={p.last_run.status} /> <span title={fmtTime(p.last_run.started_at)}>{p.last_run.kind} · {ago(p.last_run.started_at)}</span></> : '–'}
                    </td>
                    <td className="px-3 py-1.5 whitespace-nowrap">
                      {running
                        ? <span className="text-[11px] px-1.5 py-0.5 rounded border bg-sky-50 text-sky-700 border-sky-200 inline-flex items-center gap-1"><Loader2 size={11} className="animate-spin" /> Running</span>
                        : <span className={`text-[11px] px-1.5 py-0.5 rounded border ${st.cls}`}>{st.label}</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="bg-slate-50 text-slate-600 font-medium">
              <tr className="border-t border-slate-200">
                <td className="px-3 py-1.5">Total</td>
                <td className="px-2 py-1.5 text-right">{tot.docs || 0}</td>
                <td className="px-2 py-1.5 text-right">{tot.fetched || 0}</td>
                <td className="px-2 py-1.5 text-right">{tot.not_fetched || 0}</td>
                <td className="px-2 py-1.5 text-right">{tot.ocr_done || 0} / {tot.ocr_err || 0}</td>
                <td className="px-2 py-1.5 text-right">{tot.s3_ok || 0} / {tot.s3_err || 0} / {tot.s3_pending || 0}</td>
                <td className="px-2 py-1.5 text-right">{tot.local_files ? <>{tot.local_files} · {fmtBytes(tot.local_bytes)}</> : 0}</td>
                <td colSpan={2} />
              </tr>
            </tfoot>
          </table>
        </div>
      ))}
    </div>
  );
}

function Detail({ strucId }) {
  const { data, err, loading, load } = useStatus(strucId, 15000);
  const [open, setOpen] = useState(true);
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState(null);
  const d = data && data.detail;
  const p = data && (data.projects || []).find((x) => x.struc_id === strucId);
  const retry = async (stage, ids) => {
    setBusy(stage); setMsg(null);
    try {
      if (stage === 'ocr') await api('/api/solman-docs/ocr', { method: 'POST', body: JSON.stringify({ strucId, phioIds: ids, retryErrors: true }) });
      else await api('/api/solman-docs/fetch', { method: 'POST', body: JSON.stringify({ strucId, phioIds: ids, force: true }) });
      setMsg({ ok: true, text: stage === 'ocr'
        ? `OCR retry started for ${ids.length} document(s).`
        : `Re-fetch started for ${ids.length} document(s). Run the pipeline (or Upload S3) afterwards to upload them.` });
      setTimeout(load, 3000);
    } catch (e) { setMsg({ ok: false, text: e.message }); } finally { setBusy(''); }
  };
  if (err) return <div className="px-4 py-2 text-xs text-rose-600 border-b border-slate-100">Status: {err}</div>;
  if (!d || !p) return <div className="px-4 py-2 text-xs text-slate-400 border-b border-slate-100 flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> Loading process status…</div>;
  const groups = { fetch: [], s3: [], ocr: [] };
  for (const x of d.problems || []) (groups[x.stage] || groups.ocr).push(x);
  return (
    <div className="px-4 py-2 border-b border-slate-100 text-xs">
      <div className="flex items-center justify-between gap-2">
        <button type="button" onClick={() => setOpen((o) => !o)} className="flex items-center gap-1.5 font-semibold text-slate-600">
          {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />} Process status
          <span className="font-normal text-slate-500">
            · fetched {p.fetched}/{p.docs} · OCR {p.ocr_done}{p.ocr_err ? <span className="text-rose-600"> ({p.ocr_err} failed)</span> : null}
            · S3 {p.s3_ok}{p.s3_err ? <span className="text-rose-600"> ({p.s3_err} failed)</span> : null}{p.s3_pending ? <> · {p.s3_pending} pending upload</> : null}
            {p.local_files ? <> · {p.local_files} local ({fmtBytes(p.local_bytes)})</> : null}
          </span>
        </button>
        <button type="button" onClick={load} title="Refresh" className="p-1 rounded hover:bg-slate-100 text-slate-500">
          {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
        </button>
      </div>
      {open && (
        <div className="mt-2 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-2">
            {STEP_ORDER.map(([k, label]) => {
              const r = d.lastByKind[k];
              return (
                <div key={k} className="rounded-lg border border-slate-200 px-2.5 py-1.5 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium text-slate-700">{label}</span>
                    {r ? <RunBadge status={r.status} /> : <span className="text-slate-300">never</span>}
                  </div>
                  {r && <>
                    <div className="text-slate-400 flex items-center gap-1"><Clock size={11} /> {fmtTime(r.started_at)} · {ago(r.started_at)}{r.username ? ' · ' + r.username : ''}</div>
                    {r.detail && <div className="text-slate-600 truncate" title={r.detail}>{r.detail}</div>}
                  </>}
                </div>
              );
            })}
          </div>

          {(groups.fetch.length + groups.s3.length + groups.ocr.length) > 0 ? (
            <div className="space-y-1.5">
              {[['fetch', 'Fetch failed', 'Re-fetch'], ['s3', 'S3 upload failed', 'Re-fetch'], ['ocr', 'OCR failed', 'Retry OCR']].map(([k, label, act]) => groups[k].length ? (
                <details key={k} className="rounded-lg border border-amber-200 bg-amber-50/40">
                  <summary className="px-2.5 py-1.5 cursor-pointer flex items-center justify-between gap-2">
                    <span className="text-amber-800 font-medium inline-flex items-center gap-1"><AlertTriangle size={12} /> {label}: {groups[k].length}</span>
                    <button type="button" disabled={!!busy} onClick={(e) => { e.preventDefault(); retry(k === 'ocr' ? 'ocr' : 'fetch', groups[k].map((x) => x.phio_id)); }}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-amber-300 text-amber-800 hover:bg-white disabled:opacity-50">
                      {busy ? <Loader2 size={11} className="animate-spin" /> : <RotateCcw size={11} />} {act} all
                    </button>
                  </summary>
                  <ul className="px-2.5 pb-2 space-y-0.5 max-h-56 overflow-auto">
                    {groups[k].map((x) => (
                      <li key={x.phio_id} className="flex items-start justify-between gap-2">
                        <span className="min-w-0">
                          <span className="text-slate-700">{x.descript || x.phio_id}</span>
                          <span className="text-slate-400"> · {x.doc_format}</span>
                          <span className="block text-rose-600 truncate" title={x.fetch_error || x.s3_error || x.ocr_error || ''}>{x.fetch_error || x.s3_error || x.ocr_error || ''}</span>
                        </span>
                        <button type="button" disabled={!!busy} onClick={() => retry(k === 'ocr' ? 'ocr' : 'fetch', [x.phio_id])}
                          title={act} className="p-1 rounded hover:bg-white text-amber-800 disabled:opacity-50 shrink-0"><RotateCcw size={11} /></button>
                      </li>
                    ))}
                  </ul>
                </details>
              ) : null)}
            </div>
          ) : (
            <div className="text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 size={13} /> No failed documents.</div>
          )}
          {msg && <div className={msg.ok ? 'text-emerald-700' : 'text-rose-600'}>{msg.text}</div>}

          <details>
            <summary className="cursor-pointer text-slate-600 font-medium">Run history ({d.history.length})</summary>
            <table className="w-full mt-1">
              <tbody>
                {d.history.map((r) => (
                  <tr key={r.id} className="border-t border-slate-100 align-top">
                    <td className="py-1 pr-2 text-slate-400 whitespace-nowrap">{fmtTime(r.started_at)}</td>
                    <td className="py-1 pr-2 text-slate-700 whitespace-nowrap">{r.kind}</td>
                    <td className="py-1 pr-2"><RunBadge status={r.status} /></td>
                    <td className="py-1 pr-2 text-slate-500 whitespace-nowrap">{r.username || ''}</td>
                    <td className="py-1 text-slate-600 break-words">{r.detail}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </details>
        </div>
      )}
    </div>
  );
}

export default function SolmanStatusPanel({ mode = 'overview', strucId, selected, onSelect }) {
  return mode === 'detail' ? <Detail strucId={strucId} /> : <Overview selected={selected} onSelect={onSelect} />;
}
